// The wireframe's `zi-banda` — the first thing every role meets on Acasă.
//
// One shape for all of them (`wireframes/react/src/pages/Acasa.tsx:120`, "aceeași
// formă pe toate rolurile"): a low band coloured by state, a circled icon, the state's
// title and only the critical detail beside it, the actions on the right. An operator
// sees whether they have reported, an administrator how many of their locations have,
// an observer what they may look at.
//
// It replaces the greeting-and-card-grid the application opened with, which the
// wireframe has on no role: a greeting says nothing, and four equal cards say nothing
// about which one matters today.
//
// AGE ships no banner of this kind — `mud-toast` is a message, not a status
// surface with actions — so this is a SIMSM composite over AGE primitives and AGE
// tokens. No fork, no new token.
import type { ReactNode } from 'react';
import { MudIcon } from '@egov-moldova/mud-react';
import type { IconName } from '@egov-moldova/mud';

/** The states the wireframe colours for; `critical` is its `zi-banda critic`, the
    alert rule file's "Alertă declanșată" (AdminAlerte.tsx:3237, mud.css:7016). */
export type DayStatus = 'reported' | 'draft' | 'not-reported' | 'info' | 'critical';

export interface DayStatusBandProps {
  status: DayStatus;
  title: string;
  /** Only the critical detail. The wireframe is explicit that nothing else belongs here. */
  detail?: ReactNode;
  /** Primary first; the wireframe gives at most two. */
  actions?: ReactNode;
  /** The glyph, where the page's own meaning is not the state's default one. */
  icon?: IconName | undefined;
}

const ICON: Record<DayStatus, IconName> = {
  reported: 'checkmark-large',
  draft: 'clock',
  'not-reported': 'plus-large',
  info: 'chart',
  critical: 'notification',
};

export function DayStatusBand({
  status,
  title,
  detail,
  actions,
  icon,
}: DayStatusBandProps): JSX.Element {
  return (
    <div className={`simsm-day-band simsm-day-band--${status}`} role="status">
      <span className="simsm-day-band__icon" aria-hidden="true">
        {/* The wireframe draws this glyph at 22px; MUD's icon sizes are 16/20/24/32,
            so 24 is the nearest rung. Two pixels inside a 40px circle. */}
        <MudIcon name={icon ?? ICON[status]} size={24} />
      </span>
      <div className="simsm-day-band__text">
        <div className="simsm-day-band__title">{title}</div>
        {detail ? <div className="simsm-day-band__detail">{detail}</div> : null}
      </div>
      {actions ? <div className="simsm-day-band__actions">{actions}</div> : null}
    </div>
  );
}
